import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import styles from '../styles/JobList.module.scss';
import VideoPlayer from './VideoPlayer';
import { useVideoThumbnail } from '../useVideoThumbnail';
import { useShake } from '../ShakeProvider';

function getRemainingSeconds(job, nowMs) {
  if (!job) return null;

  const status = job.status;
  if (status !== 'pending' && status !== 'in_progress') return null;

  const waitSec = Number(job.estimated_wait_time || 0);
  const procSec = Number(job.estimated_processing_time || 0);

  // If there is no estimate at all, don't show anything
  if (!waitSec && !procSec) return null;

  const createdMs = Date.parse(job.created_time || job.createdTime || '');
  const startMs = Date.parse(job.startTime || job.start_time || '');

  let doneAt = null;

  if (status === 'pending') {
    if (Number.isFinite(createdMs)) {
      doneAt = createdMs + (waitSec + procSec) * 1000;
    }
  } else if (status === 'in_progress') {
    if (Number.isFinite(startMs)) {
      doneAt = startMs + procSec * 1000;
    } else if (Number.isFinite(createdMs)) {
      doneAt = createdMs + (waitSec + procSec) * 1000;
    }
  }

  if (!doneAt) return null;

  const remainingSec = (doneAt - nowMs) / 1000;

  // If already past or over 2 hours, don't show ETA
  if (remainingSec <= 0) return null;
  if (remainingSec > 7200) return null;

  return remainingSec;
}

function formatRemaining(remainingSec) {
  const s = Math.round(remainingSec);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  return `${h} h`;
}

const StatusBadge = ({ status }) => {
  const label = (status || '').toLowerCase();
  return <span className={`${styles.badge} ${styles['badge--' + label]}`}>{status}</span>;
};

export default function JobList({ template }) {
  const { startCreateNew, openJob, drafts, loadDrafts, openDraft, removeDraft, refetch, projectId } = useShake();

  useEffect(() => {
    loadDrafts(template.id);
  }, [template.id, loadDrafts]);

  const jobs =
    template.jobs.sort((a, b) => {
      const aDate = new Date(a.created_time) ? new Date(a.created_time).getTime() : 0;
      const bDate = new Date(b.created_time) ? new Date(b.created_time).getTime() : 0;
      return bDate - aDate;
    }) || [];

  const videos = template.example_videos || [];
  const [active, setActive] = useState(videos[0] ? videos[0] : null);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);

  const handleRefresh = () => {
    if (projectId) {
      refetch(projectId);
    }
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={`${styles.createBtn} shake__btnPrimary`}
        onClick={startCreateNew}
      >
        Create new
      </button>

      {/* Drafts */}
      {drafts.length > 0 && (
        <>
          <h4 className={styles.h4}>Drafts</h4>
          <ul className={styles.list}>
            {drafts.map((d) => (
              <li key={d.savedAt} className={styles.draftItem}>
                <div className={styles.jobHeader}>
                  <span className={styles.jobName}>{d.name}</span>
                  <div className={styles.actionsRow}>
                    <button className={styles.linkBtn} onClick={() => openDraft(d)}>
                      Open draft
                    </button>
                    <button className={styles.linkBtn} onClick={() => removeDraft(d.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className={styles.jobsHeader}>
        <h4 className={styles.h4}>Jobs</h4>
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={handleRefresh}
          title="Refresh"
          aria-label="Refresh jobs"
        >
          <RefreshCw size={18} />
        </button>
      </div>
      {jobs.length === 0 && <div className={styles.empty}>No jobs yet</div>}

      <ul className={styles.list}>
        {jobs.map((j) => {
          const remaining = getRemainingSeconds(j, now);
          const etaLabel = remaining != null ? formatRemaining(remaining) : null;

          return (
            <li
              key={j.id}
              className={styles.jobItem}
              onClick={() => {
                openJob(j);
              }}
              role="button"
            >
              <div className={styles.jobHeader}>
                <span className={styles.jobName}>{j.name}</span>
                <div className={styles.statusBlock}>
                  {etaLabel && <span className={styles.etaText}>~{etaLabel} left</span>}
                  {j.status && <StatusBadge status={j.status} />}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Sample videos */}
      {videos.length > 0 && (
        <>
          <h4 className={styles.h4}>Sample</h4>

          <VideoPlayer src={active.url} title={active.name} />

          <div className={styles.thumbRow} role="list">
            {videos.map((v) => {
              const isActive = v.name === active.name;
              return (
                <VideoThumb
                  key={v.name}
                  url={v.url}
                  name={v.name}
                  active={isActive}
                  onClick={() => {
                    if (!isActive) setActive(v);
                  }}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function VideoThumb({ url, name, active, onClick, displayName = true }) {
  const thumb = useVideoThumbnail(url);

  return (
    <button onClick={onClick} className={active ? styles.thumbActive : styles.thumb}>
      {thumb ? (
        <img src={thumb} alt={name} className={styles.thumbImg} />
      ) : (
        <div className={styles.thumbPlaceholder} />
      )}

      {displayName && <div className={styles.thumbName}>{name}</div>}
    </button>
  );
}
